<template>
  <span
    v-if="!isInteractive"
    class="dp-membership-badge"
    :class="[sizeClass, tierClass, noBackgroundClass]"
  >
    <img class="dp-membership-badge__icon" :src="iconSrc" alt="" />
    <span class="dp-membership-badge__label">{{ label }}</span>
  </span>
  <div v-else>
    <b-button
      :id="resolvedBadgeId"
      type="button"
      class="dp-membership-badge dp-membership-badge--interactive"
      :class="[sizeClass, tierClass, noBackgroundClass]"
      @click.stop
    >
      <img class="dp-membership-badge__icon" :src="iconSrc" alt="" />
      <span class="dp-membership-badge__label">{{ label }}</span>
    </b-button>

    <b-tooltip
      v-if="tooltipText"
      :target="resolvedBadgeId"
      triggers="hover focus"
      placement="topleft"
      custom-class="dp-membership-tooltip"
    >
      {{ tooltipText }}
      <div v-if="upgradeLink">
        <br />
        <a class="dp-membership-tooltip__link" :href="upgradeLink.url">{{ upgradeLink.label }}</a>
      </div>
    </b-tooltip>
  </div>
</template>

<script>
import iconCore from './../../assets/images/membership-core.svg';
import iconPremium from './../../assets/images/membership-premium.svg';

const TIER_CORE = 'core';
const TIER_PREMIUM = 'premium';

const SIZE_LARGE = 'large';
const SIZE_SMALL = 'small';
const SIZE_SLIM = 'slim';
const SIZE_XSMALL = 'xsmall';
const SIZES = [SIZE_LARGE, SIZE_SMALL, SIZE_SLIM, SIZE_XSMALL];

export default {
  name: 'DpMembershipBadge',
  props: {
    tierName: {
      type: String,
      required: true,
      validator: (value) => [TIER_CORE, TIER_PREMIUM].includes(String(value).toLowerCase()),
    },
    label: {
      type: String,
      required: true,
    },
    size: {
      type: String,
      required: false,
      default: SIZE_SMALL,
      validator: (value) => SIZES.includes(value),
    },
    noBackground: {
      type: Boolean,
      required: false,
      default: false,
    },
    tooltipText: {
      type: String,
      required: false,
      default: null,
    },
    upgradeLink: {
      type: Object,
      required: false,
      default: null,
    },
    badgeId: {
      type: String,
      required: false,
      default: null,
    },
  },
  computed: {
    normalizedTier() {
      return String(this.tierName).toLowerCase();
    },
    tierClass() {
      return `dp-membership-badge--${this.normalizedTier}`;
    },
    sizeClass() {
      return `dp-membership-badge--${this.size}`;
    },
    noBackgroundClass() {
      return this.noBackground ? 'dp-membership-badge--no-background' : '';
    },
    iconSrc() {
      return this.normalizedTier === TIER_PREMIUM ? iconPremium : iconCore;
    },
    isInteractive() {
      return Boolean(this.tooltipText);
    },
    resolvedBadgeId() {
      return this.badgeId || `dp-membership-badge-${this._uid}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/_variables';

$dp-membership-premium-bg: #f0eacf;

.dp-membership-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  background-color: transparent;
  border: none;
  padding: 0;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;

    img {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__label {
    line-height: inherit;
  }

  // ---- Sizes ----
  &--large {
    gap: 4px;
    padding: 4px 8px;
    font-size: 14px;
    line-height: 1.25;

    .dp-membership-badge__icon {
      width: 12px;
      height: 12px;
    }
  }

  &--small {
    gap: 4px;
    padding: 4px 8px;
    font-size: 12px;
    line-height: 14px;

    .dp-membership-badge__icon {
      width: 10px;
      height: 10px;
    }
  }

  &--slim {
    gap: 4px;
    padding: 2px 4px;
    font-size: 10px;
    line-height: 1.25;
    letter-spacing: 0.2px;

    .dp-membership-badge__icon {
      width: 10px;
      height: 10px;
    }
  }

  &--no-background {
    padding: 0;
    background-color: transparent !important;
  }

  &--xsmall {
    gap: 2px;
    font-size: 10px;
    line-height: 1.25;
    letter-spacing: 0.2px;

    .dp-membership-badge__icon {
      width: 8px;
      height: 8px;
    }
  }

  &--core,
  &--core:hover,
  &--core:active,
  &--core:focus {
    color: $sky-800;
  }

  &--core.dp-membership-badge--large,
  &--core.dp-membership-badge--small,
  &--core.dp-membership-badge--slim {
    background-color: $blue-100;
  }

  &--premium,
  &--premium:hover,
  &--premium:active,
  &--premium:focus {
    color: $amber-900;
  }

  &--premium.dp-membership-badge--large,
  &--premium.dp-membership-badge--small,
  &--premium.dp-membership-badge--slim {
    background-color: $dp-membership-premium-bg;
  }

  // ---- Interactive (when wrapped with a tooltip) ----
  &--interactive {
    cursor: pointer;

    // Strip BootstrapVue's default button affordances so the badge keeps its
    // design-system look on hover / focus / active.
    &:hover,
    &:active,
    &:focus,
    &:not(:disabled):not(.disabled):active {
      box-shadow: none !important;
      filter: brightness(0.96);
    }
  }
}

::v-deep .tooltip.dp-membership-tooltip {
  &.b-tooltip,
  &.b-tooltip:hover,
  &.b-tooltip:focus {
    opacity: 1;
  }

  & .arrow::before {
    border-top-color: $blue-900;
    border-bottom-color: $blue-900;
  }

  .tooltip-inner {
    background-color: $blue-900;
    color: $dp-white;
    font-size: 1rem;
    text-align: left;
    padding: 0.625rem 0.75rem;
  }

  .dp-membership-tooltip__link {
    color: $dp-white;
    text-decoration: underline;
  }
}
</style>
